export const ORDER_TIMELINE_STEPS = [
  { key: 'placed', label: 'Order Placed', description: 'Payment received' },
  { key: 'confirmed', label: 'Confirmed', description: 'Confirmed by admin' },
  { key: 'in_process', label: 'In Process', description: 'Pickup scheduled' },
  { key: 'dispatched', label: 'Dispatched', description: 'Picked up by courier' },
  { key: 'out_for_delivery', label: 'Out for Delivery', description: 'Courier is on the way' },
  { key: 'delivered', label: 'Delivered', description: 'Package delivered' },
];

export const TIMELINE_LABELS = {
  placed: 'Placed',
  confirmed: 'Confirmed',
  in_process: 'Processing',
  dispatched: 'Shipped',
  out_for_delivery: 'Out For Delivery',
  delivered: 'Delivered',
};

const STEP_RANK = {
  placed: 0,
  awaiting_confirmation: 0,
  confirmed: 1,
  in_process: 2,
  dispatched: 3,
  out_for_delivery: 4,
  delivered: 5,
  rto_initiated: 6,
  returned: 7,
  cancelled: -1,
};

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function rankFromShipmentStatus(status) {
  const value = normalizeText(status);
  if (!value) return null;

  if (value.includes('delivered') && !value.includes('rto')) return 'delivered';
  if (value.includes('out_for_delivery') || value.includes('out for delivery')) return 'out_for_delivery';
  if (
    value.includes('picked_up') ||
    value.includes('pickedup') ||
    value.includes('pickup_completed') ||
    value.includes('in_transit') ||
    value.includes('intransit') ||
    value.includes('dispatched')
  ) {
    return 'dispatched';
  }
  if (
    value.includes('pickup_scheduled') ||
    value.includes('pickup_generated') ||
    value.includes('pickupscheduled') ||
    value.includes('pickupgenerated')
  ) {
    return 'in_process';
  }

  return null;
}

function rankFromPickupStatus(status) {
  const value = normalizeText(status);
  if (!value) return null;

  if (value === 'delivered') return 'delivered';
  if (value.includes('picked')) return 'dispatched';
  if (value.includes('scheduled') || value.includes('pickup_scheduled')) return 'in_process';

  return null;
}

export function formatStatusLabel(value) {
  if (!value) return 'Not available';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getEffectiveTimelineKey(order) {
  if (!order) return 'placed';
  if (order.order_status === 'cancelled') return 'cancelled';

  const ranks = [
    STEP_RANK[order.order_status],
    STEP_RANK[rankFromShipmentStatus(order.shipment_status)],
    STEP_RANK[rankFromPickupStatus(order.pickup_status)],
  ].filter((rank) => typeof rank === 'number' && rank >= 0);

  const highestRank = ranks.length ? Math.max(...ranks) : 0;
  return ORDER_TIMELINE_STEPS[highestRank]?.key || 'placed';
}

export function getTimelineSteps(order) {
  const currentKey = getEffectiveTimelineKey(order);
  const currentIndex = ORDER_TIMELINE_STEPS.findIndex((step) => step.key === currentKey);

  return ORDER_TIMELINE_STEPS.map((step, index) => ({
    ...step,
    active: index <= currentIndex,
    completed: index < currentIndex,
    current: index === currentIndex,
  }));
}

export function getMobileTimelineSteps(order) {
  const currentKey = getEffectiveTimelineKey(order);
  const mobileSteps = [
    { key: 'confirmed', label: 'Confirmed', description: 'Confirmed by admin' },
    { key: 'dispatched', label: 'Shipped', description: 'Picked up by courier' },
    { key: 'out_for_delivery', label: 'Out For Delivery', description: 'Courier is on the way' },
    { key: 'delivered', label: 'Delivered', description: 'Package delivered' },
  ];
  
  const currentIndex = mobileSteps.findIndex((step) => step.key === currentKey);
  
  return mobileSteps.map((step, index) => ({
    ...step,
    active: index <= currentIndex,
    completed: index < currentIndex,
    current: index === currentIndex,
  }));
}

export function getShipmentStatusMessage(order) {
  const step = getEffectiveTimelineKey(order);

  switch (step) {
    case 'placed':
      return 'Waiting for admin confirmation.';
    case 'confirmed':
      return 'Order confirmed. Pickup will be scheduled soon.';
    case 'in_process':
      return 'Pickup has been scheduled. Courier pickup is in process.';
    case 'dispatched':
      return 'Your order has been picked up and is on the way.';
    case 'out_for_delivery':
      return 'Your order is out for delivery.';
    case 'delivered':
      return 'Your order has been delivered.';
    case 'rto_initiated':
      return 'Return to origin has been initiated.';
    case 'returned':
      return 'Your order has been returned.';
    default:
      return 'Shipment updates will appear here.';
  }
}

export function getDeliveryBarStatus(order) {
  const step = getEffectiveTimelineKey(order);
  const labels = {
    placed: {
      text: 'Order Placed - Waiting for Admin Confirmation',
      color: 'bg-yellow-500/10 border-yellow-500/30',
      textColor: 'text-yellow-400',
    },
    confirmed: {
      text: 'Order Confirmed',
      color: 'bg-emerald-500/10 border-emerald-500/30',
      textColor: 'text-emerald-400',
    },
    in_process: {
      text: 'Pickup Scheduled - In Process',
      color: 'bg-orange-500/10 border-orange-500/30',
      textColor: 'text-orange-400',
    },
    dispatched: {
      text: 'Dispatched - On the Way',
      color: 'bg-sky-500/10 border-sky-500/30',
      textColor: 'text-sky-400',
    },
    out_for_delivery: {
      text: 'Out for Delivery',
      color: 'bg-purple-500/10 border-purple-500/30',
      textColor: 'text-purple-400',
    },
    delivered: {
      text: 'Delivered',
      color: 'bg-green-500/10 border-green-500/30',
      textColor: 'text-green-400',
    },
    rto_initiated: {
      text: 'RTO Initiated',
      color: 'bg-orange-500/10 border-orange-500/30',
      textColor: 'text-orange-400',
    },
    returned: {
      text: 'Returned',
      color: 'bg-gray-500/10 border-gray-500/30',
      textColor: 'text-gray-400',
    },
  };

  return labels[step] || labels.placed;
}
